import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { LogoLoader } from '@gokrew/ui';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/CustomButton';
import { submitMcpConsentDecision } from '@/api/mcp-consent';
import type {
  McpConsentDecisionPayload,
  McpConsentDecisionResponse,
} from '@/types/mcp-consent';
import { AuthV2Layout } from '../auth-v2/components/AuthV2Layout';
import layoutStyles from '../auth-v2/components/AuthV2Layout.module.css';
import styles from './McpConsent.module.css';

interface ParsedConsentRequest {
  clientId: string;
  responseType: string;
  redirectUri: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  state?: string;
  clientName: string | null;
}

/**
 * The MCP OAuth consent screen. `/authorize` redirects here (instead of
 * silently issuing a code) the first time a user encounters a given MCP
 * client — this is the explicit approval step that closes the consent-bypass
 * gap dynamic client registration otherwise opens up. Approving/denying
 * posts back to `/authorize/decision`, which returns the final redirect URL.
 */
export function McpConsentPage() {
  const { t } = useTranslation(TranslationNS.McpConsent);
  const [searchParams] = useSearchParams();
  const [pendingDecision, setPendingDecision] = useState<
    'approve' | 'deny' | null
  >(null);

  const parsedRequest = useMemo<ParsedConsentRequest | null>(() => {
    const clientId = searchParams.get('client_id');
    const responseType = searchParams.get('response_type');
    const redirectUri = searchParams.get('redirect_uri');
    const codeChallenge = searchParams.get('code_challenge');
    const codeChallengeMethod = searchParams.get('code_challenge_method');

    if (
      !clientId ||
      !responseType ||
      !redirectUri ||
      !codeChallenge ||
      !codeChallengeMethod
    ) {
      return null;
    }

    return {
      clientId,
      responseType,
      redirectUri,
      codeChallenge,
      codeChallengeMethod,
      state: searchParams.get('state') ?? undefined,
      clientName: searchParams.get('client_name'),
    };
  }, [searchParams]);

  const requestedHost = useMemo(() => {
    if (!parsedRequest) return null;
    try {
      return new URL(parsedRequest.redirectUri).host;
    } catch {
      return null;
    }
  }, [parsedRequest]);

  const mutation = useMutation<
    McpConsentDecisionResponse,
    AxiosError<{ error_description?: string }>,
    'approve' | 'deny'
  >({
    mutationFn: (decision) => {
      if (!parsedRequest) {
        return Promise.reject(new Error('Missing authorization request'));
      }

      const payload: McpConsentDecisionPayload = {
        client_id: parsedRequest.clientId,
        response_type: parsedRequest.responseType,
        redirect_uri: parsedRequest.redirectUri,
        code_challenge: parsedRequest.codeChallenge,
        code_challenge_method: parsedRequest.codeChallengeMethod,
        state: parsedRequest.state,
        decision,
      };
      return submitMcpConsentDecision(payload);
    },
    onMutate: (decision) => setPendingDecision(decision),
    onSuccess: (data) => {
      window.location.replace(data.redirect_url);
    },
    onError: (error) => {
      setPendingDecision(null);
      toast.error(error.response?.data?.error_description ?? t('server_error'));
    },
  });

  if (!parsedRequest) {
    return (
      <AuthV2Layout>
        <div className={layoutStyles.formInner}>
          <div className={layoutStyles.formCard}>
            <p className={styles.serverError}>{t('invalid_request')}</p>
          </div>
        </div>
      </AuthV2Layout>
    );
  }

  return (
    <AuthV2Layout>
      <div className={layoutStyles.formInner}>
        <div className={layoutStyles.formCard}>
          <h2 className={styles.heading}>{t('heading')}</h2>
          <p className={styles.description}>
            {t('description', {
              client: parsedRequest.clientName || t('unknown_client'),
            })}
          </p>

          {requestedHost && (
            <div className={styles.redirectNotice}>
              <span className={styles.redirectLabel}>
                {t('redirect_label')}
              </span>
              <span className={styles.redirectHost}>{requestedHost}</span>
            </div>
          )}

          <div className={styles.actions}>
            <Button
              variant="outline"
              className={styles.denyBtn}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('deny')}
            >
              {mutation.isPending && pendingDecision === 'deny' ? (
                <LogoLoader size="sm" />
              ) : (
                t('deny')
              )}
            </Button>
            <Button
              variant="primary"
              className={styles.approveBtn}
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('approve')}
            >
              {mutation.isPending && pendingDecision === 'approve' ? (
                <LogoLoader size="sm" />
              ) : (
                t('approve')
              )}
            </Button>
          </div>
        </div>
      </div>
    </AuthV2Layout>
  );
}
